import {
    CommunityMemberRole,
    CommunityMemberSupportListEntryDto,
    CommunityMemberTag
} from '@api/ChallengeClient';
import { Filters } from '@components/filters';
import { useError } from '@components/hooks/useError';
import {
    CellProps,
    ReactColumn,
    SortingRule,
    TableComponent
} from '@components/table';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Api } from '@core/api';
import { links } from '@core/router';
import { injectTSDI } from '@core/tsdi';
import { Download } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { EditJoinDayDialog } from '@components/edit-join-day-dialog';
import { observer } from 'mobx-react';
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useCommunityMembersStore } from './store';

type VerifiedFilter = '' | 'true' | 'false';

const roleFilterOptions = [
    { label: 'All', value: '' },
    { label: 'Member', value: CommunityMemberRole.MEMBER },
    { label: 'Manager', value: CommunityMemberRole.MANAGER }
];

const verifiedFilterOptions = [
    { label: 'All', value: '' },
    { label: 'Verified', value: 'true' },
    { label: 'Pending', value: 'false' }
];

const getRoleBadgeVariant = (
    memberRole: CommunityMemberRole
): 'info' | 'outline' =>
    memberRole === CommunityMemberRole.MANAGER ? 'info' : 'outline';

const CommunityMembers = observer(() => {
    const { communityId } = useParams();
    const { googleUserAuth, gatewayApiUrl } = injectTSDI(Api);
    const showError = useError();
    const [exporting, setExporting] = React.useState(false);

    const {
        communityMembers,
        searchParams,
        memberRoleFilter,
        verifiedFilter,
        sorting,
        setSorting,
        setMemberRoleFilter,
        setVerifiedFilter,
        setSearchTerm,
        updateMemberTags,
        updateMemberJoinDay,
        updateMemberRole,
        dispatch
    } = useCommunityMembersStore(Number(communityId));

    const [joinDayMember, setJoinDayMember] =
        React.useState<CommunityMemberSupportListEntryDto | null>(null);

    const onExport = async () => {
        setExporting(true);
        try {
            const response = await fetch(
                `${gatewayApiUrl('challenge-api')}/support/v1/community-members/export?${new URLSearchParams(
                    {
                        communityId: communityId!,
                        ...(memberRoleFilter
                            ? { memberRole: memberRoleFilter }
                            : {}),
                        ...(verifiedFilter
                            ? { verified: String(verifiedFilter === 'true') }
                            : {}),
                        ...(searchParams.search
                            ? { search: searchParams.search }
                            : {})
                    }
                )}`,
                {
                    headers: {
                        authorization: `Bearer ${googleUserAuth?.accessToken}`
                    }
                }
            );
            const blob = await response.blob();
            const data = new Blob([blob], { type: 'text/csv' });
            const csvURL = window.URL.createObjectURL(data);
            const tempLink = document.createElement('a');
            tempLink.href = csvURL;
            tempLink.setAttribute(
                'download',
                `community-members-${communityId}-${format(new Date(), 'yyyy-MM-dd')}.csv`
            );
            tempLink.click();
        } catch (e) {
            showError(e);
        } finally {
            setExporting(false);
        }
    };

    const onSortingChange = (sorting?: SortingRule) => {
        if (sorting) {
            setSorting(sorting);
        }
    };

    const columns: ReactColumn<CommunityMemberSupportListEntryDto>[] = [
        {
            Header: 'ID',
            accessor: 'id',
            disableSortBy: true,
            style: { width: 90 }
        },
        {
            Header: 'Name',
            accessor: 'name',
            style: { width: 220 },
            Cell: ({
                row: {
                    original: { name, userId }
                }
            }: CellProps<CommunityMemberSupportListEntryDto, string>) => (
                <Link
                    to={links.userDetails(userId)}
                    className="text-sm font-semibold hover:underline"
                >
                    {name}
                </Link>
            )
        },
        {
            Header: 'User ID',
            accessor: 'userId',
            disableSortBy: true,
            style: { width: 180 },
            Cell: ({
                value
            }: CellProps<CommunityMemberSupportListEntryDto, string>) => (
                <span className="text-sm">{value}</span>
            )
        },
        {
            Header: 'Join Date',
            accessor: 'joinDay',
            style: { width: 120 },
            Cell: ({
                value
            }: CellProps<CommunityMemberSupportListEntryDto, string>) => (
                <span className="text-sm">
                    {format(parseISO(value), 'dd.MM.yy')}
                </span>
            )
        },
        {
            Header: 'Role',
            accessor: 'memberRole',
            style: { width: 120 },
            Cell: ({
                value
            }: CellProps<
                CommunityMemberSupportListEntryDto,
                CommunityMemberRole
            >) => <Badge variant={getRoleBadgeVariant(value)}>{value}</Badge>
        },
        {
            Header: 'Verified',
            accessor: 'verified',
            disableSortBy: true,
            style: { width: 120 },
            Cell: ({
                value
            }: CellProps<CommunityMemberSupportListEntryDto, boolean>) => (
                <Badge variant={value ? 'success' : 'outline'}>
                    {value ? 'Verified' : 'Pending'}
                </Badge>
            )
        },
        {
            Header: 'Tags',
            accessor: 'tags',
            disableSortBy: true,
            style: { width: 180 },
            Cell: ({
                value
            }: CellProps<
                CommunityMemberSupportListEntryDto,
                CommunityMemberTag[]
            >) => (
                <div className="flex flex-wrap gap-1">
                    {value?.map((tag) => (
                        <Badge key={tag} variant="warning">
                            Out of Competition
                        </Badge>
                    ))}
                </div>
            )
        }
    ];

    return (
        <>
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
                <div className="flex flex-wrap items-end gap-3">
                    <Filters<CommunityMemberRole | '', false>
                        label="Role"
                        value={memberRoleFilter}
                        options={roleFilterOptions}
                        onChange={(value) =>
                            setMemberRoleFilter(
                                value as CommunityMemberRole | ''
                            )
                        }
                        width={180}
                    />
                    <Filters<VerifiedFilter, false>
                        label="Verified"
                        value={verifiedFilter}
                        options={verifiedFilterOptions}
                        onChange={(value) =>
                            setVerifiedFilter(value as VerifiedFilter)
                        }
                        width={180}
                    />
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    loading={exporting}
                    onClick={onExport}
                >
                    <Download />
                    Export CSV
                </Button>
            </div>

            <TableComponent<CommunityMemberSupportListEntryDto[]>
                columns={columns}
                emptyText="No members found"
                items={communityMembers}
                searchPlaceholder="Search by name or user ID..."
                tableLayout="auto"
                onSortingChange={onSortingChange}
                sorting={Array.isArray(sorting) ? sorting[0] : sorting}
                onSearchChange={setSearchTerm}
                onChangePage={(page) => dispatch({ type: 'page', page })}
                onPageSizeChange={(size) => dispatch({ type: 'size', size })}
                contextMenu={[
                    {
                        label: (member: CommunityMemberSupportListEntryDto) =>
                            member.tags?.includes(
                                CommunityMemberTag.OUT_OF_COMPETITION
                            )
                                ? 'Remove Out of Competition'
                                : 'Mark Out of Competition',
                        action: async (
                            member: CommunityMemberSupportListEntryDto
                        ) => {
                            try {
                                const hasTag = member.tags?.includes(
                                    CommunityMemberTag.OUT_OF_COMPETITION
                                );
                                await updateMemberTags(
                                    member.id,
                                    hasTag
                                        ? []
                                        : [
                                              CommunityMemberTag.OUT_OF_COMPETITION
                                          ]
                                );
                            } catch (e) {
                                showError(e);
                            }
                        }
                    },
                    {
                        label: (member: CommunityMemberSupportListEntryDto) =>
                            member.memberRole === CommunityMemberRole.MANAGER
                                ? 'Make Member'
                                : 'Make Manager',
                        action: async (
                            member: CommunityMemberSupportListEntryDto
                        ) => {
                            try {
                                await updateMemberRole(
                                    member.id,
                                    member.memberRole ===
                                        CommunityMemberRole.MANAGER
                                        ? CommunityMemberRole.MEMBER
                                        : CommunityMemberRole.MANAGER
                                );
                            } catch (e) {
                                showError(e);
                            }
                        }
                    },
                    {
                        label: () => 'Edit Join Date',
                        action: (member: CommunityMemberSupportListEntryDto) =>
                            setJoinDayMember(member)
                    }
                ]}
            />
            <EditJoinDayDialog
                open={Boolean(joinDayMember)}
                joinDay={joinDayMember?.joinDay}
                onClose={() => setJoinDayMember(null)}
                onSubmit={async (joinDay) => {
                    try {
                        await updateMemberJoinDay(joinDayMember!.id, joinDay);
                        setJoinDayMember(null);
                    } catch (e) {
                        showError(e);
                    }
                }}
            />
        </>
    );
});

export default CommunityMembers;
